import { i18nFilter } from "app/filters/i18nFilter";
import { find, isEmpty, sum, sumBy, mapValues } from "lodash";
import dayjs from "dayjs";
import {
    addPartialDataZones,
    isPartialDataPoint,
} from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { ALL_CHANNELS, legendsAndChannelsObjects, legendTypes, singleModeLegends } from "./SearchOverviewGraphConfig";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

const sortByDateAsc = (array) => {
    return array.sort(({ key }, { keyB }) => dayjs.utc(key).unix() - dayjs.utc(keyB).unix());
};

export const parseSingleModeData = ({
    rawData,
    legendItems,
    rawDataMetricName,
    chartType,
    granularity,
    lastSupportedDate,
}) => {
    try {
        if (rawData && Object.keys(rawData).length > 0) {
            const rawChartData = rawData[rawDataMetricName].Data.BreakDown;
            if (chartType === chartTypes.AREA) {
                return parsePercentageData(rawChartData, legendItems, granularity, lastSupportedDate, chartType);
            } else if (chartType === chartTypes.LINE) {
                // line chart
                const parsedMetricDataObject = {};
                Object.entries(rawChartData).map(([date, object]) => {
                    Object.entries(object).map(([channel, value]) => {
                        if (channel !== ALL_CHANNELS) {
                            if (!parsedMetricDataObject[channel]) {
                                parsedMetricDataObject[channel] = [];
                            }
                            parsedMetricDataObject[channel].push({
                                Key: date,
                                Value: value ?? 0,
                            });
                        }
                    });
                });
                const singleGraphChartData = legendItems.map((legendItem) => {
                    const key = parsedMetricDataObject[legendItem.rawName] ? legendItem.rawName : null;
                    const itemsData = key
                        ? sortByDateAsc(parsedMetricDataObject[key]).map((item, index, items) => ({
                              x: dayjs.utc(item.Key).valueOf(),
                              y: item.Value,
                              isPartial: isPartialDataPoint(index, items, item, granularity, lastSupportedDate),
                          }))
                        : [];

                    return {
                        name: legendItem.name,
                        color: legendItem.color,
                        visible: legendItem.visible,
                        data: itemsData,
                    };
                });
                return addPartialDataZones(singleGraphChartData.map(sortDataPointsByDate), {
                    chartType,
                });
            }
        } else {
            return [];
        }
    } catch (e) {
        return [];
    }
};

export const parseCompareModeData = ({
    rawData,
    legendItems,
    rawDataMetricName,
    chartType,
    selectedChannel,
    granularity,
    lastSupportedDate,
}) => {
    try {
        if (rawData && Object.keys(rawData).length > 0) {
            const rawChartData = rawData[rawDataMetricName][selectedChannel].BreakDown;
            if (chartType === chartTypes.AREA) {
                // percentage chart
                return parsePercentageData(rawChartData, legendItems, granularity, lastSupportedDate, chartType);
            } else if (chartType === chartTypes.LINE) {
                // line chart
                const highchartsObj = {};
                sortByDateAsc(Object.entries(rawChartData)).map(([date, valuesArr], index, dataItems) => {
                    Object.entries(valuesArr).map(([site, value]: [string, number]) => {
                        if (!highchartsObj[site]) {
                            const siteObj = find(legendItems, (legend) => legend.name === site);
                            highchartsObj[site] = {
                                ...siteObj,
                                data: [],
                                y: [],
                            };
                        }
                        const tick = dayjs.utc(date).valueOf();
                        highchartsObj[site].data.push({
                            x: tick,
                            y: value > 0 ? value : null,
                            isPartial: isPartialDataPoint(
                                index,
                                dataItems,
                                { Key: tick },
                                granularity,
                                lastSupportedDate,
                            ),
                        });
                        highchartsObj[site].y.push({
                            channelName: site,
                            Key: date,
                            Value: value > 0 ? value : null,
                        });
                    });
                });
                return addPartialDataZones(Object.values(highchartsObj).map(sortDataPointsByDate), {
                    chartType,
                });
            }
        }
        return [];
    } catch (e) {
        return [];
    }
};

const sortDataPointsByDate = (config) => {
    const data = config?.data ?? [];
    return {
        ...config,
        data: data.slice().sort((item1, item2) => {
            let d1: number;
            let d2: number;
            if (Array.isArray(item1)) {
                [[d1], [d2]] = [item1, item2];
            } else {
                [{ x: d1 }, { x: d2 }] = [item1, item2];
            }
            if (isNaN(d1) || isNaN(d2)) {
                return 0;
            }
            return d1 - d2;
        }),
    };
};

const parsePercentageData = (rawChartData, legendItems, granularity, lastSupportedDate, chartType) => {
    try {
        const highchartsObj = {};
        const visibleLegendItems = legendItems.filter(({ visible }) => !!visible);
        sortByDateAsc(Object.keys(rawChartData)).map((date, index, dataItems) => {
            const dataObject = Object.fromEntries(
                Object.entries(rawChartData[date]).filter(([key]) => {
                    return !!visibleLegendItems?.find(({ rawName }) => rawName === key);
                }),
            );
            const totalDataObj = parseRelativeValuesData(dataObject);
            visibleLegendItems?.map((legendItem) => {
                const { rawName } = legendItem;
                if (rawName !== legendTypes.AllChannels) {
                    if (!highchartsObj[rawName]) {
                        highchartsObj[rawName] = {
                            name: legendsAndChannelsObjects?.[rawName]
                                ? i18nFilter()(legendsAndChannelsObjects?.[rawName]?.text)
                                : rawName,
                            data: [],
                            y: [],
                            visible: legendItem.visible,
                            color: legendItem.color,
                        };
                    }
                    const tick = dayjs.utc(date).valueOf();
                    highchartsObj[rawName].data.push({
                        x: tick,
                        y: totalDataObj[rawName] ?? 0,
                        isPartial: isPartialDataPoint(index, dataItems, { Key: tick }, granularity, lastSupportedDate),
                    });
                    highchartsObj[rawName].y.push({
                        channelName: rawName,
                        Key: date,
                        Value: totalDataObj[rawName] ?? 0,
                    });
                }
            });
        });

        return addPartialDataZones(Object.values(highchartsObj), { chartType });
    } catch (e) {
        return {};
    }
};

const parseRelativeValuesData = (rawTotalData) => {
    if (isEmpty(rawTotalData)) {
        return null;
    }
    const totalSum = rawTotalData[legendTypes.AllChannels]
        ? rawTotalData[legendTypes.AllChannels]
        : sum(Object.values(rawTotalData).map((value) => (value ? value : 0)));
    const mappedObj = mapValues(rawTotalData, (value) => (value ? value / totalSum : 0));
    const [[keyOfLast, valueOfLast], ...allOthers] = Object.entries(mappedObj).slice().reverse();
    const sumOfAllOthers = sumBy(allOthers, (keyValue) => keyValue[1]);
    return {
        ...mappedObj,
        [keyOfLast]: valueOfLast ? (sumOfAllOthers + valueOfLast !== 1 ? 1 - sumOfAllOthers : valueOfLast) : 0,
    };
};

const parseLegendsData = (rawTotalData, chartType, showAllChannel = false) => {
    switch (chartType) {
        case chartTypes.AREA:
            const areaResult = {};
            for (const [key, value] of Object.entries(rawTotalData)) {
                if (key !== legendTypes.AllChannels) {
                    // filter out legends with 'all channels' for area chart
                    areaResult[key] = value;
                }
            }
            return parseRelativeValuesData(areaResult);
        case chartTypes.LINE:
            const lineResult = {};
            for (const [key, value] of Object.entries(rawTotalData)) {
                if ((value as number) > 0 && (showAllChannel || key !== legendTypes.AllChannels)) {
                    // filter out legends with values less than 0 AND 'all channels' for line chart
                    lineResult[key] = value;
                }
            }
            return lineResult;
        default:
            return rawTotalData;
    }
};

const parseTotalData = ({
    rawData,
    isSingleMode,
    rawDataMetricName,
    selectedChannel,
    chartType,
    showAllChannel = false,
}) => {
    const total = isSingleMode
        ? rawData[rawDataMetricName]?.Data?.Total
        : rawData[rawDataMetricName][selectedChannel]?.Total;
    return parseLegendsData(total, chartType, showAllChannel);
};

////////////   Legends initializations //////////

export const initComparedSitesAsLegends = ({
    rawData,
    isSingleMode,
    selectedChannel,
    chosenSites,
    rawDataMetricName,
    legendsLabelsFormatter,
    chartType,
    getSiteColor,
    isMobileWeb,
}) => {
    try {
        const rawTotalData = parseTotalData({
            rawData,
            isSingleMode,
            rawDataMetricName,
            selectedChannel,
            chartType,
        });
        const legendsData = parseLegendsData(rawTotalData, chartType);
        const ComparedSitesLegendsArr = chosenSites.reduce((acc, site) => {
            const currentLegendValue =
                legendsData[site] && legendsData[site] > 0
                    ? legendsLabelsFormatter({
                          value: legendsData[site],
                      })
                    : "N/A";
            if (currentLegendValue !== "N/A" || isMobileWeb) {
                acc.push({
                    name: site,
                    rawName: site,
                    color: getSiteColor(site),
                    visible: currentLegendValue !== "N/A",
                    isDisabled: currentLegendValue === "N/A",
                    data: currentLegendValue,
                });
            }
            return acc;
        }, []);
        return ComparedSitesLegendsArr;
    } catch (e) {
        return [];
    }
};

export const initChannelSourcesAsLegends = ({
    rawData,
    isSingleMode,
    selectedChannel,
    rawDataMetricName,
    legendsLabelsFormatter,
    chartType,
    category,
    showAllChannel,
}) => {
    try {
        const rawTotalData = parseTotalData({
            rawData,
            isSingleMode,
            rawDataMetricName,
            selectedChannel,
            chartType,
            showAllChannel,
        });
        const legendsData = parseLegendsData(rawTotalData, chartType, showAllChannel);
        const getLegendName = (legendItemName) => {
            switch (rawDataMetricName) {
                case "AverageDuration":
                    return "All Duration";
                case "PagesPerVisit":
                    return "All page visits";
                default:
                    return legendItemName;
            }
        };

        const isPerentage = chartType === chartTypes.AREA;

        const channelSourcesLegendsArr = singleModeLegends[category.id].reduce((acc, legendItemName) => {
            const isAllChannels = legendItemName === legendTypes.AllChannels;

            if(isPerentage && isAllChannels) {
                return acc; // we don't want to show AllChannels in percentage mode
            }

            const currentLegendRawValue =
                legendsData[legendItemName] && legendsData[legendItemName] > 0 ? legendsData[legendItemName] : 0;
            const currentLegendFormattedValue =
                legendsData[legendItemName] && legendsData[legendItemName] > 0
                    ? legendsLabelsFormatter({
                          value: legendsData[legendItemName],
                      })
                    : "N/A";
            const legendName = isAllChannels ? getLegendName(legendItemName) : legendItemName;
            acc.push({
                rawName: legendItemName,
                name: i18nFilter()(legendsAndChannelsObjects[legendName].text),
                visible: currentLegendFormattedValue !== "N/A",
                isDisabled: currentLegendFormattedValue === "N/A",
                color: legendsAndChannelsObjects[legendName].color,
                data: currentLegendFormattedValue,
                rawValue: currentLegendRawValue,
            });

            return acc;
        }, []);
        return channelSourcesLegendsArr;
    } catch (e) {
        return [];
    }
};

const TOTAL_ORGANIC_KEY = "Search - Organic";
const TOTAL_PAID_KEY = "Search - Paid";

const getSearchChannelKeys = (channels: Record<string, unknown>) => {
    const isTotalFormat = TOTAL_ORGANIC_KEY in channels || TOTAL_PAID_KEY in channels;
    return {
        organicKey: isTotalFormat ? TOTAL_ORGANIC_KEY : legendTypes.OrganicSearch,
        paidKey: isTotalFormat ? TOTAL_PAID_KEY : legendTypes.PaidSearch,
    };
};

export const transformSingleMMXData = (data, selectedMetricTab) => {
    const clonedData = structuredClone(data);
    const breakDown = data.Data[selectedMetricTab.name].Data.BreakDown;
    const total = data.Data[selectedMetricTab.name].Data.Total;
    const { organicKey, paidKey } = getSearchChannelKeys(total);

    Object.entries(breakDown).forEach(([date, channels]) => {
        const organicVal = clonedData.Data[selectedMetricTab.name].Data.BreakDown[date]?.[organicKey] ?? 0;
        const paidVal = clonedData.Data[selectedMetricTab.name].Data.BreakDown[date]?.[paidKey] ?? 0;

        Object.keys(channels).forEach((channel) => {
            delete clonedData.Data[selectedMetricTab.name].Data.BreakDown[date][channel];
        });

        clonedData.Data[selectedMetricTab.name].Data.BreakDown[date][legendTypes.OrganicSearch] = organicVal;
        clonedData.Data[selectedMetricTab.name].Data.BreakDown[date][legendTypes.PaidSearch] = paidVal;
        clonedData.Data[selectedMetricTab.name].Data.BreakDown[date][legendTypes.AllChannels] = organicVal + paidVal;
    });

    const organicTotal = total?.[organicKey] ?? 0;
    const paidTotal = total?.[paidKey] ?? 0;

    Object.keys(total).forEach((channel) => {
        delete clonedData.Data[selectedMetricTab.name].Data.Total[channel];
    });

    clonedData.Data[selectedMetricTab.name].Data.Total[legendTypes.OrganicSearch] = organicTotal;
    clonedData.Data[selectedMetricTab.name].Data.Total[legendTypes.PaidSearch] = paidTotal;
    clonedData.Data[selectedMetricTab.name].Data.Total[legendTypes.AllChannels] = organicTotal + paidTotal;

    return clonedData;
};

export const transformCompareMMXData = (data, selectedMetricTab) => {
    const clonedData = structuredClone(data);
    const metricData = data.Data[selectedMetricTab.name];
    const { organicKey, paidKey } = getSearchChannelKeys(metricData);

    Object.keys(metricData).forEach((channel) => {
        if (!(channel === organicKey || channel === paidKey)) {
            delete clonedData.Data[selectedMetricTab.name][channel];
        }
    });

    const isTotalFormat = organicKey !== legendTypes.OrganicSearch;
    if (isTotalFormat) {
        clonedData.Data[selectedMetricTab.name][legendTypes.OrganicSearch] =
            clonedData.Data[selectedMetricTab.name][organicKey];
        delete clonedData.Data[selectedMetricTab.name][organicKey];
        clonedData.Data[selectedMetricTab.name][legendTypes.PaidSearch] =
            clonedData.Data[selectedMetricTab.name][paidKey];
        delete clonedData.Data[selectedMetricTab.name][paidKey];
    }

    const mergedTotals = Object.entries(
        clonedData.Data[selectedMetricTab.name]?.[legendTypes.OrganicSearch]?.Total || 0,
    ).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: (acc[key] || 0) + value }),
        clonedData.Data[selectedMetricTab.name]?.[legendTypes.PaidSearch]?.Total || 0,
    );
    clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels] = {
        BreakDown: {},
        Total: mergedTotals,
    };

    const organicBreakDown = clonedData.Data[selectedMetricTab.name]?.[legendTypes.OrganicSearch]?.BreakDown || {};
    // Shallow clone so the `delete` below does not mutate clonedData's PaidSearch BreakDown,
    // which would empty out the Paid Search series in compare mode.
    const paidBreakDown = { ...(clonedData.Data[selectedMetricTab.name]?.[legendTypes.PaidSearch]?.BreakDown || {}) };
    Object.entries(organicBreakDown).forEach(([date, breakdown]) => {
        if (paidBreakDown[date]) {
            const mergedBreakdown = Object.entries(organicBreakDown[date]).reduce(
                (acc, [key, value]) => ({ ...acc, [key]: (acc[key] || 0) + value }),
                paidBreakDown[date],
            );
            clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels].BreakDown = {
                ...clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels].BreakDown,
                [date]: mergedBreakdown,
            };
            delete paidBreakDown[date];
        } else {
            clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels].BreakDown = {
                ...clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels].BreakDown,
                [date]: breakdown,
            };
        }
    });
    clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels].BreakDown = {
        ...clonedData.Data[selectedMetricTab.name][legendTypes.AllChannels].BreakDown,
        ...paidBreakDown,
    };

    return clonedData;
};
